import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import {
  useAccessGroups,
  useAccessGroupMembers,
  useDoorPolicies,
  useDoors,
  useSchedules,
} from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { AccessGroup, DoorPolicy } from '../api/types'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function GroupRow({
  group,
  policies,
  doorName,
  scheduleName,
}: {
  group: AccessGroup
  policies: DoorPolicy[]
  doorName: (uid: string) => string
  scheduleName: (uid: string | null) => string | null
}) {
  const navigate = useNavigate()
  const members = useAccessGroupMembers(group.unique_id)
  return (
    <TR className="cursor-pointer" onClick={() => navigate(`/access-groups/${group.unique_id}`)}>
      <TD>
        <span className="block text-sm font-medium text-ostrel-ink">{group.name}</span>
        {group.description && (
          <span className="block truncate text-[11px] text-ostrel-ink-muted">{group.description}</span>
        )}
      </TD>
      <TD>
        <Chip tone="muted" label={String(group.priority)} />
      </TD>
      <TD>
        <span className="tnum text-xs text-ostrel-ink-muted">
          {members.data ? `${members.data.length} member${members.data.length === 1 ? '' : 's'}` : '…'}
        </span>
      </TD>
      <TD>
        {policies.length === 0 ? (
          <span className="text-xs text-ostrel-ink-muted">No door policies</span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {policies.map((p) => (
              <span key={p.unique_id} className="flex items-center gap-1.5">
                <Chip tone={p.mode === 'exclude' ? 'danger' : 'ok'} label={p.mode} />
                <span className="tnum text-[11px] text-ostrel-ink-muted">
                  {doorName(p.door)} · {scheduleName(p.schedule) ?? 'Always'}
                </span>
              </span>
            ))}
          </span>
        )}
      </TD>
    </TR>
  )
}

function CreateGroupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('0')

  const create = useMutation({
    mutationFn: () =>
      api.createAccessGroup({
        name: name.trim(),
        description: description.trim(),
        priority: Number.parseInt(priority, 10) || 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
      setPriority('0')
      create.reset()
    }
    // create.reset is stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Modal open={open} title="New access group" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Residents"
          autoFocus
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional"
        />
        <Input
          label="Priority"
          type="number"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        />
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Per door, the highest-priority group with a decision wins — including an exclude. Ties
          favor include.
        </p>
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function AccessGroupsPage() {
  const groups = useAccessGroups()
  const policies = useDoorPolicies()
  const schedules = useSchedules()
  const doors = useDoors()

  const [createOpen, setCreateOpen] = useState(false)

  const scheduleNames = useMemo(
    () => new Map((schedules.data ?? []).map((s) => [s.unique_id, s.name])),
    [schedules.data],
  )
  const scheduleName = (uid: string | null) => (uid ? (scheduleNames.get(uid) ?? uid) : null)
  const doorNames = useMemo(
    () => new Map((doors.data ?? []).map((d) => [d.unique_id, [...d.breadcrumb, d.name].join(' / ')])),
    [doors.data],
  )
  const doorName = (uid: string) => doorNames.get(uid) ?? uid

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Access"
        title="Access groups"
        action={<Button onClick={() => setCreateOpen(true)}>+ New group</Button>}
      />

      {groups.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : groups.isError ? (
        <EmptyState title="Could not load access groups" hint={errText(groups.error)} />
      ) : groups.data.length === 0 ? (
        <EmptyState
          title="No access groups yet"
          hint="Create the first group to start granting door access."
          action={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              + New group
            </Button>
          }
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Group</TH>
              <TH>Priority</TH>
              <TH>Members</TH>
              <TH>Door policies</TH>
            </tr>
          </THead>
          <TBody>
            {[...groups.data]
              .sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name))
              .map((g) => (
                <GroupRow
                  key={g.unique_id}
                  group={g}
                  policies={(policies.data ?? []).filter((p) => p.group === g.unique_id)}
                  doorName={doorName}
                  scheduleName={scheduleName}
                />
              ))}
          </TBody>
        </Table>
      )}

      <CreateGroupModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
